// lib/consent.ts
//
// Cookie consent state + helpers. Three tiers:
//
//   - "all"        → strictly necessary + analytics + attribution.
//                    Vercel Analytics, custom session-scoped page-view
//                    tracking (with anon_id), referrer + UTM capture,
//                    geographic enrichment.
//   - "necessary"  → strictly necessary only (auth, CSP nonce, AB
//                    variant cookie that drives layout). NO analytics
//                    tracking, NO third-party scripts.
//   - "rejected"   → same as "necessary" in effect — visitor opted
//                    out. We keep it as a distinct value so the
//                    banner doesn't re-prompt, and so we honour
//                    explicit refusal in any future data flows.
//
// Consent is stored in the `fm-consent` cookie (12-month TTL, per
// EU Council and Qatar PDPL recommendation for re-prompt cadence).
// Cookie is non-HttpOnly so the client banner can read it on first
// paint; sameSite=lax so a refresh from email links still carries it.

export const CONSENT_COOKIE_NAME = "fm-consent";
export const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 12 months

export type ConsentLevel = "all" | "necessary" | "rejected";

export function isValidConsent(v: unknown): v is ConsentLevel {
  return v === "all" || v === "necessary" || v === "rejected";
}

/**
 * Browser-side helper. Reads the cookie via document.cookie.
 * Returns null if no consent recorded (banner should display).
 */
export function readConsentFromDocument(): ConsentLevel | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CONSENT_COOKIE_NAME}=`))
    ?.split("=")[1];
  if (!raw) return null;
  return isValidConsent(raw) ? raw : null;
}

/**
 * Browser-side writer. Sets the cookie so subsequent renders pick up
 * the new consent without a server round-trip.
 */
export function writeConsentToDocument(level: ConsentLevel): void {
  if (typeof document === "undefined") return;
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE_NAME}=${level}; Path=/; Max-Age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}
